import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import { Calendar } from './Calendar'
import axios from 'axios'
import { useAuthStatus } from '../utils'
import { fetchTours, flattenToursResponse } from '../services/api'

const TourRegion = {
	DOMESTIC: 'DOMESTIC',
	INTERNATIONAL: 'INTERNATIONAL',
}

const Gender = {
	MALE: 'MALE',
	FEMALE: 'FEMALE',
}

// 날짜 중복 검사 유틸리티 함수
function getTourYearValue(tour) {
	const raw = tour?.startedAt ?? tour?.started_at
	if (!raw) return null
	const d = new Date(raw)
	return Number.isNaN(d.getTime()) ? null : d.getFullYear()
}

const areDatesOverlapping = (start1, end1, start2, end2) => {
	const d1_start = new Date(start1)
	const d1_end = new Date(end1)
	const d2_start = new Date(start2)
	const d2_end = new Date(end2)

	return !(d1_end < d2_start || d1_start > d2_end)
}

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT

// 'year' prop을 추가했습니다.
export const TourSection = ({
	// API_ENDPOINT,
	TourCount,
	selectOnlyOne,
	onSelectedToursChange,
	onNoToursAvailable,
	year,
}) => {
	const [tourList, setTourList] = useState([])
	const [selectedTourIds, setSelectedTourIds] = useState([])
	const [currentView, setCurrentView] = useState('list')
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [selectedDateTours, setSelectedDateTours] = useState([])
	const [modalDate, setModalDate] = useState('')
	const [showTourCount] = useState(TourCount)
	const [selectedTourCount, setSelectedTourCount] = useState('')
	const [selectedOneTour] = useState(selectOnlyOne)
	const [selectedRegion, setSelectedRegion] = useState(TourRegion.DOMESTIC)
	const [selectedTourCategory, setSelectedTourCategory] = useState(null)
	const [auth, onChangeAuthStatus] = useAuthStatus()

	const [selectedGender, setSelectedGender] = useState(null)
	// State to store user's previously selected tours
	const [userSelectedTours, setUserSelectedTours] = useState([])

	// 1. `displayYear` 변수 도입: `year` prop을 사용하거나, 없으면 현재 연도를 기본값으로 사용합니다.
	const displayYear = useMemo(() => year || new Date().getFullYear(), [year])
	const tourColorMap = useRef(new Map())

	const colorPalette = useMemo(() => ['#76E8AD', '#FF8E89', '#76E8AD'], [])

	const sendPlayerType = async () => {
		try {
			const response = await axios.get(`${API_ENDPOINT}/users/player/email`, {
				params: {
					userId: auth.user?.id,
				},
			})
			const g = response.data.playerType === 'KLPGA' ? Gender.FEMALE : Gender.MALE
			setSelectedGender(g)
			return g
		} catch (error) {
			console.error('플레이어 타입 가져오기 실패:', error)
			if (error.response) {
				console.error('에러 응답:', error.response.data)
			}
			return null
		}
	}

	// 사용자가 이전에 선택한 투어 ID 목록을 가져오는 함수
	const getPlayerSelectedTour = async () => {
		try {
			const response = await axios.get(`${API_ENDPOINT}/users/selectedTour`, {
				params: {
					userId: auth.user?.id,
				},
			})

			console.log('API 응답 (userSelectedTour):', response.data)
			// console.log("API 응답 (userSelectedTour):", response.data.userSelectedTour); // 디버깅용으로 추가
			// 응답 데이터에서 각 객체의 'tour_id' 필드만 추출하여 배열로 반환
			const list = response.data.userSelectedTour
			return Array.isArray(list) ? list.map((item) => item.tour_id) : []
		} catch (error) {
			console.error('선택목록을 가져오는 중 오류가 발생하였습니다:', error)
			return [] // 오류 발생 시 빈 배열 반환
		}
	}

	const loadTourList = async (genderForFetch) => {
		try {
			const payload = await fetchTours(genderForFetch || undefined)
			const flat = flattenToursResponse(payload)
			const updatedTourList = flat.map((tour) => {
				let color = tourColorMap.current.get(tour.id)
				if (!color) {
					const count = tourColorMap.current.size
					color = colorPalette[count % colorPalette.length]
					tourColorMap.current.set(tour.id, color)
				}
				return { ...tour, assignedColor: color }
			})
			console.log('updatedTourList:', updatedTourList)
			setTourList(updatedTourList)
			setCurrentView('list')
		} catch (error) {
			console.error('대회 목록을 가져오는 중 오류 발생:', error)
			if (error.response) {
				console.error('Axios 에러 응답:', error.response.data)
				console.error('Axios 에러 상태:', error.response.status)
				console.error('Axios 에러 헤더:', error.response.headers)
			} else if (error.request) {
				console.error('Axios 에러 요청:', error.request)
			} else {
				console.error('Axios 에러 메시지:', error.message)
			}
		}
	}

	const isTourOverlappingWithSelected = useCallback(
		(targetTour) => {
			if (selectedTourIds.length === 0) {
				return false
			}

			const currentlySelectedTours = tourList.filter((tour) =>
				selectedTourIds.includes(tour.id),
			)

			for (const selected of currentlySelectedTours) {
				if (
					areDatesOverlapping(
						selected.startedAt,
						selected.endedAt,
						targetTour.startedAt,
						targetTour.endedAt,
					)
				) {
					return true
				}
			}
			return false
		},
		[selectedTourIds, tourList],
	)

	const handleCheckboxToggle = (tourId) => {
		let newSelectedTourIds
		const toggledTour = tourList.find((tour) => tour.id === tourId)

		if (!toggledTour) return

		if (selectedOneTour) {
			newSelectedTourIds = (prev) => (prev.includes(tourId) ? [] : [tourId])
		} else {
			if (selectedTourIds.includes(tourId)) {
				newSelectedTourIds = (prev) => prev.filter((id) => id !== tourId)
			} else {
				if (isTourOverlappingWithSelected(toggledTour)) {
					console.log('이 투어는 이미 선택된 다른 투어와 기간이 겹칩니다.')
					return
				}
				newSelectedTourIds = (prev) => [...prev, tourId]
			}
		}
		setSelectedTourIds(newSelectedTourIds)

		if (onSelectedToursChange && typeof onSelectedToursChange === 'function') {
			onSelectedToursChange(newSelectedTourIds)
		}
	}

	useEffect(() => {
		setSelectedTourCount(selectedTourIds.length)
	}, [selectedTourIds])

	const handleRegionChange = (region) => {
		setSelectedRegion(region)
		setSelectedTourCategory(null)
	}

	const handleGenderChange = (gender) => {
		setSelectedGender(gender)
		setSelectedTourCategory(null)
		setSelectedTourIds([])
		if (onSelectedToursChange) onSelectedToursChange([])
	}

	const handleTourCategoryClick = (categoryString) => {
		setSelectedTourCategory((prevCategory) =>
			prevCategory === categoryString ? null : categoryString,
		)
	}

	const handleDateClick = (info) => {
		const clickedDate = info.dateStr
		setModalDate(clickedDate)

		const matched = tourList.filter((tour) => {
			const isDateMatch = clickedDate >= tour.startedAt && clickedDate <= tour.endedAt
			const isRegionMatch = tour.region === selectedRegion
			let isCategoryMatch = true
			let isTypeMatch = true

			if (selectedRegion === TourRegion.INTERNATIONAL) {
				let genderSpecificCategories = []
				if (selectedGender === Gender.MALE) {
					genderSpecificCategories = ['KPGA', 'ASIAN', 'DP', 'JGTO', 'PGA', 'KORNFERRY']
				} else if (selectedGender === Gender.FEMALE) {
					genderSpecificCategories = ['KLPGA', 'LPGA', 'JLPGA', 'LET']
				}

				isCategoryMatch = genderSpecificCategories.includes(tour.TourCategory)

				if (selectedTourCategory) {
					isCategoryMatch = isCategoryMatch && tour.TourCategory === selectedTourCategory
				}
			} else {
				isCategoryMatch =
					!selectedTourCategory || tour.TourCategory === selectedTourCategory

				if (selectedGender === Gender.MALE) {
					isTypeMatch = tour.type === 'KPGA'
				} else if (selectedGender === Gender.FEMALE) {
					isTypeMatch = tour.type === 'KLPGA'
				}
			}
			return isDateMatch && isRegionMatch && isCategoryMatch && isTypeMatch
		})
		setSelectedDateTours(matched)
		setIsModalOpen(true)
	}

	const closeModal = () => {
		setIsModalOpen(false)
		setSelectedDateTours([])
		setModalDate('')
	}

	useEffect(() => {
		const initializeTours = async () => {
			let genderForFetch = null
			if (auth.user && auth.user.id) {
				genderForFetch = await sendPlayerType()
				const userSelected = await getPlayerSelectedTour()
				console.log(userSelected)
				setUserSelectedTours(userSelected)
			}
			await loadTourList(genderForFetch)
		}
		initializeTours()
		// eslint-disable-next-line react-hooks/exhaustive-deps -- loadTourList/sendPlayerType는 auth에 묶인 클로저
	}, [auth.user?.id])

	// 2. `thisYearTourList`에서 `currentYear` 대신 `displayYear`를 사용합니다.
	const thisYearTourList = useMemo(() => {
		let filteredTours = tourList.filter((tour) => {
			const tourYear = getTourYearValue(tour)
			const isYearMatch = tourYear === displayYear // displayYear 사용
			const isRegionMatch = tour.region === selectedRegion
			let isCategoryMatch = true
			let isTypeMatch = true

			if (selectedRegion === TourRegion.INTERNATIONAL) {
				if (selectedGender === Gender.MALE) {
					const maleCategories = ['KPGA', 'ASIAN', 'DP', 'JGTO', 'PGA', 'KORNFERRY']
					isCategoryMatch = maleCategories.includes(tour.TourCategory)
				} else if (selectedGender === Gender.FEMALE) {
					const femaleCategories = ['KLPGA', 'LPGA', 'JLPGA', 'LET']
					isCategoryMatch = femaleCategories.includes(tour.TourCategory)
				}
				if (selectedTourCategory) {
					isCategoryMatch = isCategoryMatch && tour.TourCategory === selectedTourCategory
				}
			} else {
				// 국내 (DOMESTIC)
				isCategoryMatch =
					!selectedTourCategory || tour.TourCategory === selectedTourCategory

				if (selectedGender === Gender.MALE) {
					isTypeMatch = tour.type === 'KPGA'
				} else if (selectedGender === Gender.FEMALE) {
					isTypeMatch = tour.type === 'KLPGA'
				}
			}

			return isYearMatch && isRegionMatch && isCategoryMatch && isTypeMatch
		})

		// Apply filtering based on userSelectedTours if it's not empty
		if (userSelectedTours.length > 0) {
			filteredTours = filteredTours.filter((tour) => userSelectedTours.includes(tour.id))
		}

		return filteredTours
	}, [
		tourList,
		displayYear,
		selectedRegion,
		selectedGender,
		selectedTourCategory,
		userSelectedTours,
	]) // Add userSelectedTours to dependencies

	// 3. `onNoToursAvailable`를 호출하는 `useEffect`에서도 `currentYear` 대신 `displayYear`를 사용합니다.
	useEffect(() => {
		if (onNoToursAvailable) {
			const hasParticipatableTours = tourList.some((tour) => {
				const tourYear = getTourYearValue(tour)
				if (tourYear !== displayYear) return false // displayYear 사용
				if (selectedGender === null) return true
				if (selectedGender === Gender.MALE) {
					const maleCategories = ['KPGA', 'ASIAN', 'DP', 'JGTO', 'PGA', 'KORNFERRY']
					return tour.type === 'KPGA' || maleCategories.includes(tour.TourCategory)
				}
				if (selectedGender === Gender.FEMALE) {
					const femaleCategories = ['KLPGA', 'LPGA', 'JLPGA', 'LET']
					return tour.type === 'KLPGA' || femaleCategories.includes(tour.TourCategory)
				}
				return false
			})
			onNoToursAvailable(!hasParticipatableTours)
		}
	}, [tourList, selectedGender, displayYear, onNoToursAvailable]) // 의존성 배열에 `displayYear` 추가

	const convertToursToCalendarEvents = (tours) => {
		return tours.map((tour) => {
			const end = new Date(tour.endedAt)
			end.setDate(end.getDate() + 1)
			return {
				id: tour.id,
				title: '',
				start: tour.startedAt,
				end: end.toISOString().split('T')[0],
				allDay: true,
				backgroundColor: tour.assignedColor,
				borderColor: tour.assignedColor,
				display: 'background',
				extendedProps: {
					purse: tour.purse,
					venue: tour.venue,
					type: tour.type,
					TourCategory: tour.TourCategory,
				},
			}
		})
	}

	const calendarEvents = useMemo(
		() => convertToursToCalendarEvents(thisYearTourList),
		[thisYearTourList],
	)

	return (
		<div className="tour-list">
			<div className="select-domestic-overseas">
				{showTourCount && (
					<div className="tour-select-message">
						대회를 선택해 주세요. (총{' '}
						<span className="target-count">{selectedTourCount}</span>경기)
					</div>
				)}
				{selectedOneTour && (
					<div className="one-tour-select-message">
						대회를 선택해 주세요. (
						<span className="target-one-count">{selectedTourCount}</span>/1)
					</div>
				)}
				<div className="domestic-or-overseas">
					<div
						className={`domestic ${selectedRegion === TourRegion.DOMESTIC ? 'active' : ''}`}
						onClick={() => handleRegionChange(TourRegion.DOMESTIC)}>
						국내
					</div>
					<div
						className={`overseas ${selectedRegion === TourRegion.INTERNATIONAL ? 'active' : ''}`}
						onClick={() => handleRegionChange(TourRegion.INTERNATIONAL)}>
						해외
					</div>
				</div>
			</div>

			<div className="view-container">
				{selectedRegion === TourRegion.INTERNATIONAL && (
					<div>
						{selectedGender === Gender.MALE ? (
							<div className="tour-variation-man">
								<>
									<div
										className={selectedTourCategory === 'KPGA' ? 'active' : ''}
										onClick={() => handleTourCategoryClick('KPGA')}>
										KPGA
									</div>
									<div
										className={selectedTourCategory === 'ASIAN' ? 'active' : ''}
										onClick={() => handleTourCategoryClick('ASIAN')}>
										ASIAN
									</div>
									<div
										className={selectedTourCategory === 'DP' ? 'active' : ''}
										onClick={() => handleTourCategoryClick('DP')}>
										DP
									</div>
									<div
										className={selectedTourCategory === 'JGTO' ? 'active' : ''}
										onClick={() => handleTourCategoryClick('JGTO')}>
										JGTO
									</div>
									<div
										className={selectedTourCategory === 'PGA' ? 'active' : ''}
										onClick={() => handleTourCategoryClick('PGA')}>
										PGA
									</div>
									<div
										className={
											selectedTourCategory === 'KORNFERRY' ? 'active' : ''
										}
										onClick={() => handleTourCategoryClick('KORNFERRY')}>
										KORNFERRY
									</div>
								</>
							</div>
						) : (
							<div className="tour-variation-woman">
								<>
									<div
										className={selectedTourCategory === 'KLPGA' ? 'active' : ''}
										onClick={() => handleTourCategoryClick('KLPGA')}>
										KLPGA
									</div>
									<div
										className={selectedTourCategory === 'LPGA' ? 'active' : ''}
										onClick={() => handleTourCategoryClick('LPGA')}>
										LPGA
									</div>
									<div
										className={selectedTourCategory === 'JLPGA' ? 'active' : ''}
										onClick={() => handleTourCategoryClick('JLPGA')}>
										JLPGA
									</div>
									<div
										className={selectedTourCategory === 'LET' ? 'active' : ''}
										onClick={() => handleTourCategoryClick('LET')}>
										LET
									</div>
								</>
							</div>
						)}
					</div>
				)}
				{thisYearTourList.length > 0 && (
					<div className="watch-method">
						<div
							className={`view-toggle ${currentView === 'list' ? 'active-view' : ''}`}
							onClick={() => setCurrentView('list')}>
							<img
								src="/images/register-hamburger.svg"
								alt="icon"
								className="register-hamburger-icon"
							/>
							<div className="watch-content-list">목록으로 보기</div>
						</div>
						<div
							className={`view-toggle ${currentView === 'calendar' ? 'active-view' : ''}`}
							onClick={() => setCurrentView('calendar')}>
							<img
								src="/images/register-calender.svg"
								alt="icon"
								className="register-calendar-icon"
							/>
							<div className="watch-content-calendar">달력으로 보기</div>
						</div>
					</div>
				)}

				{currentView === 'list' ? (
					<div
						className={`tour-list-container ${thisYearTourList.length === 0 ? 'no-tours-background' : ''}`}>
						{thisYearTourList.length > 0 ? (
							thisYearTourList.map((tour) => {
								const isOverlapping =
									selectedTourIds.length > 0 &&
									!selectedTourIds.includes(tour.id) &&
									isTourOverlappingWithSelected(tour)
								const isCheckboxDisabled =
									(selectedOneTour &&
										selectedTourIds.length > 0 &&
										!selectedTourIds.includes(tour.id)) ||
									isOverlapping

								return (
									<div
										key={tour.id}
										className={`tour-item custom-checkbox-container ${isCheckboxDisabled ? 'disabled-tour-item' : ''}`}
										onClick={() =>
											!isCheckboxDisabled && handleCheckboxToggle(tour.id)
										}>
										<input
											type="checkbox"
											id={`tour-${tour.id}`}
											checked={selectedTourIds.includes(tour.id)}
											readOnly
											className="tour-checkbox"
											disabled={isCheckboxDisabled}
										/>
										<div className="tour-details">
											<div className="tour-date-container">
												<div className="date-bar"></div>
												<div className="tour-dates">
													{tour.startedAt} ~ {tour.endedAt}
												</div>
											</div>
											<div className="tour-name">{tour.name}</div>
											<div className="tour-purse">
												총상금{' '}
												{Number(tour.purse ?? 0).toLocaleString('ko-KR')}원
											</div>
										</div>
									</div>
								)
							})
						) : (
							<div className="no-tours-message">
								현재 예정된 대회 목록이 없어요.<br></br>추후 대회 일정이 업데이트
								되면, 알려드릴게요!
							</div>
						)}
					</div>
				) : (
					<Calendar
						initialDate={new Date().toISOString().split('T')[0]}
						events={calendarEvents}
						onClickEvent={(info) =>
							console.log('캘린더 이벤트 클릭 (TourSection):', info.event)
						}
						onClickDate={handleDateClick}
					/>
				)}
			</div>

			{isModalOpen && (
				<div className="modal-overlay" onClick={closeModal}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()}>
						<h2 className="modal-day-title">
							{modalDate}일 <br></br>대회 목록
						</h2>
						{selectedDateTours.length > 0 ? (
							selectedDateTours.map((tour) => {
								const isOverlapping =
									selectedTourIds.length > 0 &&
									!selectedTourIds.includes(tour.id) &&
									isTourOverlappingWithSelected(tour)
								const isCheckboxDisabled =
									(selectedOneTour &&
										selectedTourIds.length > 0 &&
										!selectedTourIds.includes(tour.id)) ||
									isOverlapping

								return (
									<div
										key={tour.id}
										className={`modal-tour-item custom-checkbox-container ${isCheckboxDisabled ? 'disabled-tour-item' : ''}`}
										onClick={() =>
											!isCheckboxDisabled && handleCheckboxToggle(tour.id)
										}>
										<input
											type="checkbox"
											id={`modal-tour-${tour.id}`}
											checked={selectedTourIds.includes(tour.id)}
											readOnly
											className="tour-checkbox"
											disabled={isCheckboxDisabled}
										/>
										<div className="tour-details">
											<div className="tour-date-container">
												<div className="date-bar"></div>
												<div className="tour-dates">
													{tour.startedAt} ~ {tour.endedAt}
												</div>
											</div>
											<div className="tour-name">{tour.name}</div>
											<div className="tour-purse">
												총상금{' '}
												{Number(tour.purse ?? 0).toLocaleString('ko-KR')}원
											</div>
										</div>
									</div>
								)
							})
						) : (
							<p>이 날짜에는 대회가 없습니다.</p>
						)}
						<button onClick={closeModal}>닫기</button>
					</div>
				</div>
			)}
		</div>
	)
}
